import React, { useState, useMemo, useEffect, useRef } from 'react';
import { GridColDef } from '@mui/x-data-grid-premium';
import { useTableSettings, tableSettingsProps, useInitialTableSettings, useCompleteTableSettings } from 'src/core/tables/useTableSettings';
import { useTableSelector, MergeResult } from 'src/core/tables/useTableSelector';
import { useGetTransferProducts } from 'src/pages/warehouses/warehouseRequests';
import { ID_FIELD_PRODUCTS } from 'src/core/tables/helpers/idFields';
import tableHelper from 'src/core/tables/tableHelper';
import CustomButton from 'src/core/ui/CustomButton';
import DeleteIcon from '@mui/icons-material/Delete';

export function useSelectProductOnTransfer(initialValues: any, saveResult?: any, warehouseValues = initialValues, editable = true): [GridColDef[], tableSettingsProps, any[], ReturnType<typeof useTableSelector<any>>] {
  const [rows, setRows] = useState<any[]>(initialValues?.TransferOrderRows ?? []);
  const fromWarehouseId = getWarehouseId(warehouseValues?.FromWarehouse) ?? getWarehouseId(warehouseValues?.FromWarehouseId);
  const toWarehouseId = getWarehouseId(warehouseValues?.ToWarehouse) ?? getWarehouseId(warehouseValues?.ToWarehouseId);
  const [dispCols, dispSettings] = useTransferOrderRowsTable(rows, setRows, initialValues?.OrderId, editable);
  const [selCols, selSettings, products] = useTransferProductsSelectTable(fromWarehouseId, toWarehouseId);
  const pendingTableResetRef = useRef(false);
  const rowSource = saveResult?.TransferOrderRows ?? initialValues?.TransferOrderRows;

  useEffect(() => {
    if (!Array.isArray(rowSource)) return;
    pendingTableResetRef.current = true;
    setRows(rowSource);
  }, [rowSource, initialValues?.OrderId]);
  useEffect(() => {
    if (!pendingTableResetRef.current) return;
    pendingTableResetRef.current = false;
    dispSettings.discardAllChanges();
    dispSettings.apiRef.current?.setRows?.(rows);
    dispSettings.setRowSelectionModel([]);
    selSettings.setRowSelectionModel([]);
  }, [rows, dispSettings, selSettings]);

  const receivingQtyByProductId = useMemo(() => new Map(products.map(product => [String(product.ProductId), product.ToWarehouseAvailableQty] as const)), [products]);
  useEffect(() => {
    setRows(prev => {
      let changed = false;
      const next = prev.map(row => {
        const quantity = receivingQtyByProductId.get(String(row.ProductId));
        if (row.ToWarehouseAvailableQty === quantity) return row;
        changed = true;
        return { ...row, ToWarehouseAvailableQty: quantity };
      });
      return changed ? next : prev;
    });
  }, [receivingQtyByProductId]);

  const mergeProductsIntoRows = (prev: any[], picked: TransferProduct[]): MergeResult<any> => {
    if (!editable || !picked.length) return prev;
    const next = [...prev];
    let rowNr = Math.max(0, ...prev.map(row => Number(row.RowNr) || 0));
    let tempId = Math.min(0, ...prev.map(row => Number(row.OrderRowId) || 0));
    picked.forEach(prod => {
      if (next.some(row => String(row.ProductId) === String(prod.ProductId))) return;
      next.push({ RowNr: ++rowNr, OrderRowId: --tempId, Product: prod.ProductName, ProductId: prod.ProductId, Qty: 1, QtyTransferred: 0, AvailableQtyAtTime: prod.WarehouseAvailableQty ?? 0, ToWarehouseAvailableQty: prod.ToWarehouseAvailableQty, Comment: '' });
    });
    return next;
  };
  const selector = useTableSelector<any>({ getId: row => row.ProductId, rows, setRows, selectGrid: [selCols, selSettings], onMerge: mergeProductsIntoRows, rowId: 'OrderRowId', displayTableSettings: dispSettings });
  return [dispCols, dispSettings, rows, selector];
}

export const useTransferOrderRowsTable = (rows: any[], setRows: React.Dispatch<React.SetStateAction<any[]>>, orderId: number | string, editable = true): readonly [GridColDef[], tableSettingsProps] => {
  const newTableNameRef = useRef(`TransferOrderRowsTable_new_${Math.random()}`);
  const tableName = orderId != null ? `TransferOrderRowsTable_${orderId}` : newTableNameRef.current;
  const initial = useInitialTableSettings({ tableName, idPath: ['OrderRowId'], setExternalRows: setRows, autoHeight: true, hasToolbar: false }, rows);
  const settingsRef = useRef(initial);
  settingsRef.current = initial;
  const columns: GridColDef[] = useMemo(() => [
    { field: 'RowNr', headerName: '#', width: 40, type: 'number' },
    { field: 'OrderRowId', headerName: 'Order Row Id', renderCell: tableHelper.idCell },
    { field: 'Product', headerName: 'Product Name', flex: 1 },
    { field: 'ProductId', headerName: 'Product Id' },
    { field: 'Qty', headerName: 'Qty', type: 'number', editable },
    { field: 'QtyTransferred', headerName: 'Qty Transferred', type: 'number' },
    { field: 'AvailableQtyAtTime', headerName: 'Available Qty At Time', type: 'number' },
    { field: 'ToWarehouseAvailableQty', headerName: 'Receiving Warehouse Available Qty', type: 'number', width: 200 },
    { field: 'Comment', headerName: 'Comment', editable, flex: 1 },
    ...(editable ? [{ field: 'actions', getActions: params => [<CustomButton key={params.id} iconButton title="Remove product" icon={<DeleteIcon />} onClick={() => { settingsRef.current.discardRowChanges(params.id); setRows(previous => previous.filter(row => row.OrderRowId !== params.id)); }} />], type: 'actions', headerName: 'Delete', width: 80 } as GridColDef] : []),
  ], [rows, setRows, editable]);
  const tableSettings = useCompleteTableSettings({ columns }, initial);
  tableSettings.loading = false;
  return [columns, tableSettings] as const;
};

export const useTransferProductsSelectTable = (fromWarehouseId: number | string | null, toWarehouseId?: number | string | null): readonly [GridColDef[], tableSettingsProps, TransferProduct[]] => {
  const { rows: fromRowsRaw, status: fromStatus } = useGetTransferProducts(fromWarehouseId);
  const { rows: toRowsRaw, status: toStatus } = useGetTransferProducts(toWarehouseId);
  const fromRows = Array.isArray(fromRowsRaw) ? fromRowsRaw as TransferProduct[] : EMPTY_PRODUCTS;
  const toRows = Array.isArray(toRowsRaw) ? toRowsRaw as TransferProduct[] : EMPTY_PRODUCTS;
  const columns: GridColDef[] = useMemo(() => [
    { field: 'ProductId', headerName: 'Id', width: 75, maxWidth: 85, hideable: false },
    { field: 'ProductName', headerName: 'Name', minWidth: 180, width: 190, flex: 2 },
    { field: 'ProductStatus_Value', headerName: 'Status', cellClassName: tableHelper.statusColumn, valueGetter: tableHelper.getFieldKeyOrValue, renderCell: tableHelper.renderStatusCell },
    { field: 'ManufacturerProductId', headerName: 'Manufacturer Product Id' },
    { field: 'Manufacturer_Value', headerName: 'Manufacturer', valueGetter: tableHelper.getFieldKeyOrValue },
    { field: 'WarehouseQty', headerName: 'Warehouse Qty', type: 'number', width: 200 },
    { field: 'WarehouseAvailableQty', headerName: 'Available Qty', type: 'number' },
    { field: 'ToWarehouseAvailableQty', headerName: 'Receiving Warehouse Available Qty', type: 'number', width: 200 },
    { field: 'WarehouseReservedQty', headerName: 'Reserved Qty', type: 'number' },
    { field: 'DispatchQty', headerName: 'Dispatch Qty', type: 'number' },
    { field: 'DispatchCmdQty', headerName: 'Dispatch Cmd Qty', type: 'number' },
    { field: 'ProductStatus_Key', headerName: 'Status Id', valueGetter: tableHelper.getFieldKeyOrValue, width: 80, minWidth: 70, maxWidth: 100 },
  ], []);
  const mergedRows = useMemo(() => {
    const toByProductId = new Map(toRows.map(row => [String(row.ProductId), row]));
    return fromRows.map(row => ({ ...row, ToWarehouseAvailableQty: toByProductId.get(String(row.ProductId))?.WarehouseAvailableQty ?? 0 }));
  }, [fromRows, toRows]);
  const status = fromStatus === 'loading' || toStatus === 'loading' ? 'loading' : fromStatus === 'error' || toStatus === 'error' ? 'error' : fromStatus;
  const tableSettings = useTableSettings({ tableName: `TransferProductSelectTable_${fromWarehouseId}_${String(toWarehouseId)}`, idPath: ID_FIELD_PRODUCTS, columns, rows: mergedRows, status, checkboxSelection: true, hasToolbar: false, initalSorting: [{ field: 'WarehouseAvailableQty', sort: 'desc' }] });
  useEffect(() => { tableSettings.setRowSelectionModel([]); }, [fromWarehouseId, toWarehouseId]);
  return [columns, tableSettings, mergedRows] as const;
};

export interface TransferProduct {
  ProductId: number | string;
  ProductName: string;
  ManufacturerProductId?: string;
  ProductStatus_Key?: any;
  ProductStatus_Value?: any;
  WarehouseQty?: number;
  WarehouseReservedQty?: number;
  DispatchQty?: number;
  DispatchCmdQty?: number;
  WarehouseAvailableQty?: number;
  ToWarehouseAvailableQty?: number;
}

const EMPTY_PRODUCTS: TransferProduct[] = [];
const getWarehouseId = (value: any): number | string | null => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' || typeof value === 'string') return value;
  return value.WarehouseId ?? value.Id ?? value.Key ?? null;
};
